import { useEffect, useRef, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface ImageRevealProps {
  className?: string;
  children: ReactNode;
}

/**
 * Editorial image mask reveal: frame unclips bottom → top while the
 * first <img> eases 1.06 → 1. Plays once. Initial hidden state is set
 * only from JS, so images stay visible if animation never initialises.
 */
export default function ImageReveal({ className = '', children }: ImageRevealProps) {
  const frameRef = useRef<HTMLDivElement>(null);

  // useEffect (not layout) so the Hero's pin, created earlier in tree order, exists first.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const img = frame.querySelector('img');
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        paused: true,
        defaults: { duration: 0.85, ease: 'power3.out' },
        onComplete: () => {
          gsap.set(frame, { clearProps: 'clipPath,willChange' });
          if (img) gsap.set(img, { clearProps: 'transform,willChange' });
        },
      });
      tl.fromTo(
        frame,
        { clipPath: 'inset(0% 0% 100% 0%)', willChange: 'clip-path' },
        { clipPath: 'inset(0% 0% 0% 0%)' },
        0,
      );
      if (img) tl.fromTo(img, { scale: 1.06, willChange: 'transform' }, { scale: 1 }, 0);

      ScrollTrigger.create({
        trigger: frame,
        start: 'top 85%',
        once: true,
        refreshPriority: -1,
        onEnter: () => tl.play(),
      });
    }, frame);

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <div ref={frameRef} className={className}>
      {children}
    </div>
  );
}